import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import {
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@pentabd/ui";

import { UpdateLeaveBalanceTable } from "./UpdateLeaveBalanceTable";

import { updateLeaveBalance } from "@api/leave-balance";

import { LeaveType } from "@type/HRM/leave-type";

import { STATUS } from "@constants/status.hrm";

import {
  UPDATE_LEAVE,
  UpdateLeaveFormValidationDataType,
  updateLeaveFormValidation,
} from "@validations/HRM/leave/updateLeaveFormValidation";

interface UpdateLeaveBalanceType {
  employeeID: string;
  leaveBalance: any;
  successAction: () => void;
  setIsSuccessModalOpen: (arg: boolean) => void;
  setIsErrorModalOpen: (arg: boolean) => void;
  setMessage: (arg: string) => void;
}

export const UpdateLeaveBalanceBody = ({
  employeeID,
  leaveBalance,
  successAction,
  setIsSuccessModalOpen,
  setIsErrorModalOpen,
  setMessage,
}: UpdateLeaveBalanceType) => {
  const { t } = useTranslation();

  const methods = useForm<UpdateLeaveFormValidationDataType>({
    resolver: yupResolver(updateLeaveFormValidation),
  });
  const { control, reset, handleSubmit } = methods;
  const { fields, append } = useFieldArray({
    control,
    name: "updateLeaveFormValidation",
  });

  useEffect(() => {
    reset();
    leaveBalance?.forEach((item: any) => {
      append(item);
    });
  }, [leaveBalance, append]);

  const onSubmit: SubmitHandler<UpdateLeaveFormValidationDataType> = (
    data: UpdateLeaveFormValidationDataType,
  ) => {
    const filteredData = data.updateLeaveFormValidation.map((item: any) => {
      return {
        ...item,
        [UPDATE_LEAVE.REMAINING_LEAVE]:
          Number(item[UPDATE_LEAVE.TOTAL_LEAVE]) -
          Number(item[UPDATE_LEAVE.TAKEN_LEAVE]),
      };
    });

    if (
      filteredData.filter((item: any) => item.leaveBalanceCount < 0)?.length > 0
    ) {
      setIsErrorModalOpen(true);
      setMessage("One or more remaining leave balances are negative.");
    } else {
      updateLeaveBalance(employeeID, filteredData, (e) => {
        if (e.status === STATUS.SUCCESS) setIsSuccessModalOpen(true);
        else setIsErrorModalOpen(true);
      });
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="pt-10">
        <UpdateLeaveBalanceTable data={fields as LeaveType[]} />
        <div className="d-flex justify-content-end gap-6 mt-10 pt-10 border-top">
          <Button
            size="sm"
            fill="outline"
            type="light"
            htmlType="button"
            onClick={successAction}
          >
            {t("LEAVE_UPDATE.TABLE.BUTTONS.CANCEL")}
          </Button>
          <Button size="sm" fill="fill" type="info" htmlType="submit">
            {t("LEAVE_UPDATE.TABLE.BUTTONS.SAVE")}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
